import React, { useState, ChangeEvent } from "react";
// import { Check } from 'lucide-react';
import { headerStyle, sectionStyle } from "./formConstants";
import InstaColors, { useSharedValue } from "../components/Constants/constants";
import "../Forms/style.css";
import { FormCheckBox, FormDate, FormInput, FormTitle, createRequiredLabel } from "./common";
import CountryDropdown from "./CountryDropdown";
import { W8EXPFormModel } from "../Models/W8EXPModels";
import { useW8EXPForm } from "../Hooks/useW8EXPForm";
import { W8EXPService } from "../services/W8EXPService";
import { detectTINType, validateTINFormat } from "../utils/formUtils";

interface W8EXPFormProps {
  selectedRowData?: any;
  row?: any;
  selectedIndex?: number;
  onSaveSuccess?: (row: any) => void;
}

function W8EXPFORM({ selectedRowData, row, selectedIndex = 0, onSaveSuccess }: W8EXPFormProps) {
  const { isMenuOpen } = useSharedValue();

  // Use W8EXP form hook for state management
  const { model, errors, updateField, validate } = useW8EXPForm({
    initialData: selectedRowData,
  });

  // Legacy form data state for UI compatibility
  const [formData, setFormData] = useState(() => {
    // Convert model to form data format for legacy UI
    const modelData = model as any;
    return {
      ...modelData,
    };
  });

  // Update formData whenever model changes
  React.useEffect(() => {
    setFormData({ ...model });
  }, [model]);

  // Watch for changes in selectedRowData and update form fields accordingly
  React.useEffect(() => {
    if (selectedRowData) {
      // Check isMapDone flag - if true, don't map API response values to prevent overwriting user changes
      // Check model (most current), formData (UI state), AND selectedRowData, prioritizing model and formData
      const isMapDone = model.isMapDone || formData.isMapDone || selectedRowData.isMapDone;

      if (isMapDone) {
        console.log(
          "Skipping API field mapping - isMapDone is true (model:",
          model.isMapDone,
          "formData:",
          formData.isMapDone,
          "selectedRowData:",
          selectedRowData.isMapDone,
          ")"
        );
        return;
      }

      // Check if UniqueIdentifier has changed and update it
      const newUniqueIdentifier =
        selectedRowData.uniqueidentifier || selectedRowData.UniqueIdentifier;
      const currentUniqueIdentifier = formData.UniqueIdentifier;

      if (newUniqueIdentifier && newUniqueIdentifier !== currentUniqueIdentifier) {
        updateField("UniqueIdentifier", newUniqueIdentifier);
      }

      // Auto-set certification checkbox if signature exists
      if (selectedRowData["Signature"]) {
        updateField("isSigned", "yes");
      }

      // Update other fields that might have changed
      Object.keys(selectedRowData).forEach((key) => {
        const value = selectedRowData[key];
        const currentValue = (formData as any)[key];

        // Only update if the value has actually changed and is not empty
        if (value && value !== currentValue && typeof value === "string") {
          updateField(key, value);
        }
      });

      // Auto-check GIIN checkbox if GIIN field has a value
      const giinValue = selectedRowData["GIIN"];
      if (giinValue && giinValue.toString().trim()) {
        updateField("GIINCheckBox", true);
      }

      // Auto-check Foreign TIN checkbox if Foreign tax identifying number has a value
      const foreignTinValue = selectedRowData["Foreign tax identifying number"];
      if (foreignTinValue && foreignTinValue.toString().trim()) {
        updateField("Foreign TIN", true);
      }

      // Auto-detect and set taxpayer type based on TIN values from API
      const foreignTinFromApi = selectedRowData["Foreign tax identifying number"];
      const usTinFromApi = selectedRowData["U.S. taxpayer identification number"];
      const currentTaxpayerType = formData["Taxpayer type"];

      // Check which TIN field has a value and auto-detect type
      const activeTinFromApi =
        usTinFromApi && usTinFromApi.toString().trim()
          ? usTinFromApi.toString()
          : foreignTinFromApi && foreignTinFromApi.toString().trim()
            ? foreignTinFromApi.toString()
            : null;

      if (activeTinFromApi && !currentTaxpayerType) {
        const detectedType = detectTINType(activeTinFromApi);
        if (
          detectedType &&
          (detectedType === "EIN" ||
            detectedType === "SSN/ITIN" ||
            detectedType === "SSN" ||
            detectedType === "ITIN")
        ) {
          const mappedType =
            detectedType === "SSN" || detectedType === "ITIN" ? "SSN/ITIN" : detectedType;
          updateField("Taxpayer type", mappedType);
        }
      }
    }
  }, [selectedRowData, updateField]);

  // Auto-check GIIN and Foreign TIN checkboxes when their corresponding fields have values (only if not already mapped)
  React.useEffect(() => {
    const isMapDone = formData.isMapDone;

    // Only auto-check if mapping is not done to prevent overwriting user choices
    if (!isMapDone) {
      // Auto-check GIIN checkbox when GIIN field has a value
      const giinValue = formData["GIIN"];
      const giinCheckBox = formData["GIINCheckBox"];
      if (giinValue && typeof giinValue === "string" && giinValue.trim() && !giinCheckBox) {
        updateField("GIINCheckBox", true);
      }

      // Auto-check Foreign TIN checkbox when Foreign tax identifying number has a value
      const foreignTinValue = formData["Foreign tax identifying number"];
      const foreignTinCheckBox = formData["Foreign TIN"];
      if (
        foreignTinValue &&
        typeof foreignTinValue === "string" &&
        foreignTinValue.trim() &&
        !foreignTinCheckBox
      ) {
        updateField("Foreign TIN", true);
      }
    }
  }, [
    formData["GIIN"],
    formData["Foreign tax identifying number"],
    formData["GIINCheckBox"],
    formData["Foreign TIN"],
    formData.isMapDone,
    updateField,
  ]);

  // Auto-validate TIN values when form loads or TIN/Type fields change
  React.useEffect(() => {
    const foreignTinValue = formData["Foreign tax identifying number"];
    const usTinValue = formData["U.S. taxpayer identification number"];
    const taxpayerType = formData["Taxpayer type"];

    // Check which TIN field has a value and validate accordingly
    const activeTinValue =
      usTinValue && usTinValue.toString().trim()
        ? usTinValue.toString()
        : foreignTinValue && foreignTinValue.toString().trim()
          ? foreignTinValue.toString()
          : null;

    if (activeTinValue) {
      const detectedType = detectTINType(activeTinValue);

      if (
        detectedType &&
        (detectedType === "EIN" ||
          detectedType === "SSN/ITIN" ||
          detectedType === "SSN" ||
          detectedType === "ITIN")
      ) {
        // Valid format detected - show success message
        setTinValidationMessage("✓ Valid TIN format");
      } else {
        // Invalid format - show validation message with guidance
        const typeToValidate = taxpayerType || "";
        const validationResult = validateTINFormat(activeTinValue, typeToValidate);
        setTinValidationMessage(
          validationResult.message || "⚠️ Please enter TIN in correct format"
        );
      }
    } else {
      // No TIN value - clear validation message
      setTinValidationMessage(null);
    }
  }, [
    formData["Foreign tax identifying number"],
    formData["U.S. taxpayer identification number"],
    formData["Taxpayer type"],
  ]);

  // State to control validation summary display
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // State for GIIN validation message
  const [giinValidationMessage, setGiinValidationMessage] = useState<string | null>(null);

  // State for TIN validation message
  const [tinValidationMessage, setTinValidationMessage] = useState<string | null>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, type, value, checked } = e.target as HTMLInputElement;
    const newValue = type === "checkbox" ? checked : value;

    // Update the model through the hook
    updateField(name, newValue);

    // Set isMapDone to true when user makes manual changes to prevent API overwrites
    if (!formData.isMapDone) {
      updateField("isMapDone", true);
    }

    // Auto-check GIIN checkbox when GIIN field has a value and validate format
    if (name === "GIIN" && newValue && newValue.toString().trim()) {
      updateField("GIINCheckBox", true);

      // Validate GIIN format
      const giinValue = newValue.toString().trim();
      const giinPattern = /^[A-Z0-9]{6}\.[A-Z0-9]{5}\.[A-Z0-9]{2}\.[A-Z0-9]{3}$/i;

      if (giinPattern.test(giinValue)) {
        setGiinValidationMessage("✓ Valid GIIN format");
      } else {
        setGiinValidationMessage(
          "⚠️ Invalid GIIN format. Expected: XXXXXX.XXXXX.XX.XXX (e.g., G018AA.99999.SL.777)"
        );
      }
    } else if (name === "GIIN") {
      // Clear validation message when GIIN field is empty
      setGiinValidationMessage(null);
    }

    // Auto-check Foreign TIN checkbox when Foreign tax identifying number has a value and validate TIN
    if (name === "Foreign tax identifying number" && newValue && newValue.toString().trim()) {
      updateField("Foreign TIN", true);

      // Validate TIN format and auto-detect type if valid
      const tinValue = newValue.toString().trim();
      const detectedType = detectTINType(tinValue);
      const currentTaxpayerType = formData["Taxpayer type"];

      if (
        detectedType &&
        (detectedType === "EIN" ||
          detectedType === "SSN/ITIN" ||
          detectedType === "SSN" ||
          detectedType === "ITIN")
      ) {
        // Auto-set taxpayer type if detected and no type is currently selected or if different
        if (!currentTaxpayerType || currentTaxpayerType !== detectedType) {
          const mappedType =
            detectedType === "SSN" || detectedType === "ITIN" ? "SSN/ITIN" : detectedType;
          updateField("Taxpayer type", mappedType);
        }
        setTinValidationMessage("✓ Valid TIN format");
      } else {
        // Always validate if there's a value, regardless of detected type
        const typeToValidate = currentTaxpayerType || "";
        const validationResult = validateTINFormat(tinValue, typeToValidate);
        setTinValidationMessage(
          validationResult.message || "⚠️ Please enter TIN in correct format."
        );
      }
    } else if (name === "Foreign tax identifying number") {
      // Clear validation message when TIN field is empty
      setTinValidationMessage(null);
    }

    // Handle U.S. taxpayer identification number validation
    if (name === "U.S. taxpayer identification number" && newValue && newValue.toString().trim()) {
      // Validate TIN format and auto-detect type if valid
      const tinValue = newValue.toString().trim();
      const detectedType = detectTINType(tinValue);
      const currentTaxpayerType = formData["Taxpayer type"];

      if (
        detectedType &&
        (detectedType === "EIN" ||
          detectedType === "SSN/ITIN" ||
          detectedType === "SSN" ||
          detectedType === "ITIN")
      ) {
        // Auto-set taxpayer type if detected and no type is currently selected or if different
        if (!currentTaxpayerType || currentTaxpayerType !== detectedType) {
          const mappedType =
            detectedType === "SSN" || detectedType === "ITIN" ? "SSN/ITIN" : detectedType;
          updateField("Taxpayer type", mappedType);
        }
        setTinValidationMessage("✓ Valid TIN format");
      } else {
        // Always validate if there's a value, regardless of detected type
        const typeToValidate = currentTaxpayerType || "";
        const validationResult = validateTINFormat(tinValue, typeToValidate);
        setTinValidationMessage(
          validationResult.message || "⚠️ Please enter TIN in correct format"
        );
      }
    } else if (name === "U.S. taxpayer identification number") {
      // Clear validation message when TIN field is empty
      setTinValidationMessage(null);
    }

    // Handle taxpayer type change and validate existing TINs
    if (name === "Taxpayer type" && newValue) {
      const foreignTinValue = formData["Foreign tax identifying number"];
      const usTinValue = formData["U.S. taxpayer identification number"];

      // Check which TIN field has a value and validate accordingly
      const activeTinValue =
        usTinValue && usTinValue.toString().trim()
          ? usTinValue.toString()
          : foreignTinValue && foreignTinValue.toString().trim()
            ? foreignTinValue.toString()
            : null;

      if (activeTinValue) {
        const validationResult = validateTINFormat(activeTinValue, newValue as string);

        if (validationResult.isValid) {
          setTinValidationMessage("✓ Valid TIN format");
        } else {
          setTinValidationMessage(validationResult.message || `⚠️ Invalid ${newValue} TIN format`);
        }
      } else {
        // Clear validation message when no TIN is entered
        setTinValidationMessage(null);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear any previous validation errors
    setShowValidationErrors(false);

    if (!validate()) {
      setShowValidationErrors(true);
      alert("Please fill in all required fields before submitting.");
      return;
    }

    try {
      // Use the model directly as the source of truth for current form values
      const currentFormValues: any = {};

      // Get all current form values from the model (which is always up-to-date)
      Object.keys(model).forEach((key) => {
        currentFormValues[key] = (model as any)[key];
      });

      // Set mapping done flag to prevent re-mapping
      currentFormValues.isMapDone = true;

      // Remove specific fields before saving
      currentFormValues["EXP-Permanent_address_city_state_postal"] = "";
      currentFormValues["EXP-Mailing_address_city_state_postal"] = "";
      currentFormValues["EXP-Entity_Type"] = "";
      currentFormValues["EXP-Chapter4Status"] = "";

      // Create a new model with the current form values
      const saveModel = new W8EXPFormModel(currentFormValues);

      // Save directly using the service with our collected current values
      const success = await W8EXPService.saveToApi(saveModel, row, selectedIndex);

      if (success) {
        // Clear validation errors on successful save
        setShowValidationErrors(false);

        if (onSaveSuccess) {
          onSaveSuccess(row);
        }
        alert("Form saved successfully");
      }
    } catch (error) {
      console.error("Failed to save W-8EXP form:", error);
      alert("Failed to save form data");
    }
  };

  return (
    <div className="scroll-container" style={headerStyle(isMenuOpen)}>
      <div
        style={{ width: "100%", display: "flex", flexDirection: "column", padding: 0, margin: 0 }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100% !important",
            minWidth: "100%",
            maxWidth: "100%",
            flex: "1 1 100%",
            display: "flex !important",
            flexDirection: "column",
            margin: "0 !important",
            padding: "0 !important",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
            {/* Validation Summary - Only show after user tries to submit */}
            {showValidationErrors && Object.keys(errors).length > 0 && (
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                  padding: "12px",
                  margin: "16px 0",
                  color: "#b91c1c",
                }}
              >
                <h4 style={{ margin: "0 0 8px 0", color: "#991b1b" }}>
                  Please fix the following errors:
                </h4>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {Object.entries(errors).map(([field, error], index) => (
                    <li key={index} style={{ marginBottom: "4px" }}>
                      <strong>{field.replace("EXP-", "").replace(/_/g, " ")}:</strong> {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Part I: Identification of Beneficial Owner */}
            <section style={sectionStyle}>
              <FormInput
                label={createRequiredLabel("Unique Form Identifier")}
                id={"UniqueIdentifier"}
                name={"UniqueIdentifier"}
                value={(formData["UniqueIdentifier"] as string) || ""}
                onChange={handleChange}
              />
              {showValidationErrors && errors["UniqueIdentifier"] && (
                <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                  {errors["UniqueIdentifier"]}
                </div>
              )}

              <FormTitle value={"Part I: Identification of Beneficial Owner"} />
              <FormInput
                label={"Name of the organization:"}
                id={"Name of organization"}
                name={"Name of organization"}
                value={(formData["Name of organization"] as string) || ""}
                onChange={handleChange}
              />
              {showValidationErrors && errors["Name of organization"] && (
                <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                  {errors["Name of organization"]}
                </div>
              )}
              <CountryDropdown
                label="Country of Organization:"
                name="Country_of_incorporation_organization"
                id="Country_of_incorporation_organization"
                value={
                  typeof formData["Country_of_incorporation_organization"] === "string"
                    ? formData["Country_of_incorporation_organization"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Country_of_incorporation_organization",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Other"}
                name={"Other"}
                value={(formData["Other"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginTop: 24, marginBottom: 16 }}>
                <label style={{ fontWeight: 500, display: "block", marginBottom: 8 }}>
                  Chapter 3 Status (entity type){" "}
                  <span style={{ fontWeight: 400 }}>(Must select one option only):</span>
                </label>

                {/* Original checkboxes (kept for backwards compatibility) */}
                <FormCheckBox
                  label={"Foreign Central Bank of Issue"}
                  name={"EXP-Foreign_central_bank_issue"}
                  checked={!!formData["EXP-Foreign_central_bank_issue"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Foreign Government"}
                  name={"EXP-Foreign_government"}
                  checked={!!formData["EXP-Foreign_government"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Foreign Private Foundation"}
                  name={"EXP-Foreign_private_foundation"}
                  checked={!!formData["EXP-Foreign_private_foundation"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Foreign Tax-Exempt Organization"}
                  name={"EXP-Foreign_tax-exempt_organization"}
                  checked={!!formData["EXP-Foreign_tax-exempt_organization"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Government of a U.S. territory"}
                  name={"EXP-Government_of_us_territory"}
                  checked={!!formData["EXP-Government_of_us_territory"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"International Organization"}
                  name={"EXP-International_organization"}
                  checked={!!formData["EXP-International_organization"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Withholding qualified holder under section 1445"}
                  name={"EXP-Withholding_qualified_holder"}
                  checked={!!formData["EXP-Withholding_qualified_holder"]}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginTop: 24, marginBottom: 16 }}>
                <label style={{ fontWeight: 500, display: "block", marginBottom: 8 }}>
                  Chapter 4 Status (FATCA Status):
                </label>

                {/* Original checkboxes (kept for backwards compatibility) */}
                <FormCheckBox
                  label={"501(c) organization"}
                  name={"EXP-501c_organization"}
                  checked={!!formData["EXP-501c_organization"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Direct reporting NFFE"}
                  name={"EXP-Direct_reporting_NFFE"}
                  checked={!!formData["EXP-Direct_reporting_NFFE"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Exempt retirement plan of foreign government"}
                  name={"EXP-Exempt_retirement_plan"}
                  checked={!!formData["EXP-Exempt_retirement_plan"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={
                    "Foreign government (including a political subdivision), government of a U.S. territory, or foreign central bank of issue"
                  }
                  name={"EXP-4_foreign_government_central_bank"}
                  checked={!!formData["EXP-4_foreign_government_central_bank"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"International organization"}
                  name={"EXP-4_international_organization"}
                  checked={!!formData["EXP-4_international_organization"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Nonreporting IGA FFI"}
                  name={"EXP-Nonreporting_IGA_FFI"}
                  checked={!!formData["EXP-Nonreporting_IGA_FFI"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Participating FFI"}
                  name={"EXP-Participating_FFI"}
                  checked={!!formData["EXP-Participating_FFI"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Passive NFFE"}
                  name={"EXP-Passive_NFFE"}
                  checked={!!formData["EXP-Passive_NFFE"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Registered deemed-compliant FFI"}
                  name={"EXP-Registered_deemed_compliant_FFI"}
                  checked={!!formData["EXP-Registered_deemed_compliant_FFI"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Reporting Model 1 FFI"}
                  name={"EXP-Reporting_Model1_FFI"}
                  checked={!!formData["EXP-Reporting_Model1_FFI"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Reporting Model 2 FFI"}
                  name={"EXP-Reporting_Model2_FFI"}
                  checked={!!formData["EXP-Reporting_Model2_FFI"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Sponsored direct reporting NFFE"}
                  name={"EXP-Sponsored_direct_reporting_NFFE"}
                  checked={!!formData["EXP-Sponsored_direct_reporting_NFFE"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Territory financial institution"}
                  name={"EXP-Territory_financial_institution"}
                  checked={!!formData["EXP-Territory_financial_institution"]}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Permanent address section */}
            <section style={sectionStyle}>
              <FormTitle value={"Permanent address"} />

              {/* Additional permanent address fields */}
              <FormInput
                label={createRequiredLabel("Line 1:")}
                id={"Permanent residence address"}
                name={"Permanent residence address"}
                value={(formData["Permanent residence address"] as string) || ""}
                onChange={handleChange}
              />
              {showValidationErrors && errors["Permanent residence address"] && (
                <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                  {errors["Permanent residence address"]}
                </div>
              )}
              <FormInput
                label={"Line 2:"}
                id={"Permanent address line 2"}
                name={"Permanent address line 2"}
                value={(formData["Permanent address line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"Permanent address line 3"}
                name={"Permanent address line 3"}
                value={(formData["Permanent address line 3"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City / Town:"}
                id={"Permanent address city"}
                name={"Permanent address city"}
                value={(formData["Permanent address city"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500 }}>
                    <span style={{ fontWeight: 700 }}>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent address state USCA"}
                    name={"Permanent address state USCA"}
                    value={(formData["Permanent address state USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500 }}>
                    <span style={{ fontWeight: 700 }}>Non U.S.:</span> State / Province as it
                    appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent address state NonUS"}
                    name={"Permanent address state NonUS"}
                    value={(formData["Permanent address state NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"Permanent address zip"}
                name={"Permanent address zip"}
                value={(formData["Permanent address zip"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="Permanent address country"
                id="Permanent address country"
                value={
                  typeof formData["Permanent address country"] === "string"
                    ? formData["Permanent address country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Permanent address country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Permanent address other"}
                name={"Permanent address other"}
                value={(formData["Permanent address other"] as string) || ""}
                onChange={handleChange}
              />
              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>
                    Is care-of-address: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is care of"
                      value="yes"
                      checked={formData["Permanent address is care of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is care of"
                      value="no"
                      checked={formData["Permanent address is care of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                  {showValidationErrors && errors["Permanent address is care of"] && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                      {errors["Permanent address is care of"]}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>
                    Is P.O. Box: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is po box"
                      value="yes"
                      checked={formData["Permanent address is po box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is po box"
                      value="no"
                      checked={formData["Permanent address is po box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                  {showValidationErrors && errors["Permanent address is po box"] && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                      {errors["Permanent address is po box"]}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>
                    Is rural route: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is rural route"
                      value="yes"
                      checked={formData["Permanent address is rural route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is rural route"
                      value="no"
                      checked={formData["Permanent address is rural route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                  {showValidationErrors && errors["Permanent address is rural route"] && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                      {errors["Permanent address is rural route"]}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Mailing address section */}
            <section style={sectionStyle}>
              <FormTitle value={"Mailing address"} />

              {/* Additional mailing address fields */}
              <FormInput
                label={"Line 1:"}
                id={"EXP-Mailing_address_street"}
                name={"EXP-Mailing_address_street"}
                value={(formData["EXP-Mailing_address_street"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 2:"}
                id={"Mailing address line 2"}
                name={"Mailing address line 2"}
                value={(formData["Mailing address line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"Mailing address line 3"}
                name={"Mailing address line 3"}
                value={(formData["Mailing address line 3"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City / Town:"}
                id={"Mailing city address"}
                name={"Mailing city address"}
                value={(formData["Mailing city address"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500 }}>
                    <span style={{ fontWeight: 700 }}>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"Mailing address state USCA"}
                    name={"Mailing address state USCA"}
                    value={(formData["Mailing address state USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500 }}>
                    <span style={{ fontWeight: 700 }}>Non U.S.:</span> State / Province as it
                    appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"Mailing address state NonUS"}
                    name={"Mailing address state NonUS"}
                    value={(formData["Mailing address state NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"Mailing address zip"}
                name={"Mailing address zip"}
                value={(formData["Mailing address zip"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="EXP-Mailing_address_country"
                id="EXP-Mailing_address_country"
                value={
                  typeof formData["EXP-Mailing_address_country"] === "string"
                    ? formData["EXP-Mailing_address_country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "EXP-Mailing_address_country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Mailing address other"}
                name={"Mailing address other"}
                value={(formData["Mailing address other"] as string) || ""}
                onChange={handleChange}
              />
              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>Is care-of-address:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Mailing address is care of"
                      value="yes"
                      checked={formData["Mailing address is care of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Mailing address is care of"
                      value="no"
                      checked={formData["Mailing address is care of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>Is P.O. Box:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Mailing address is po box"
                      value="yes"
                      checked={formData["Mailing address is po box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Mailing address is po box"
                      value="no"
                      checked={formData["Mailing address is po box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>Is rural route:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Mailing address is rural route"
                      value="yes"
                      checked={formData["Mailing address is rural route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Mailing address is rural route"
                      value="no"
                      checked={formData["Mailing address is rural route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
            </section>

            {/* Clean Taxpayer identification section matching the image */}
            <section style={sectionStyle}>
              <FormTitle value={"Taxpayer identification"} />
              <div style={{ marginBottom: 16 }}>
                <label
                  htmlFor="Taxpayer type"
                  style={{ fontWeight: 500, display: "block", marginBottom: 4 }}
                >
                  Type:
                </label>
                <select
                  id="Taxpayer type"
                  name="Taxpayer type"
                  value={
                    typeof formData["Taxpayer type"] === "string" ? formData["Taxpayer type"] : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "Taxpayer type",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">---Select---</option>
                  <option value="SSN/ITIN">SSN/ITIN</option>
                  <option value="EIN">EIN</option>
                </select>
              </div>

              <FormInput
                label="TIN:"
                id="U.S. taxpayer identification number"
                name="U.S. taxpayer identification number"
                value={(formData["U.S. taxpayer identification number"] as string) || ""}
                onChange={handleChange}
              />

              {/* TIN Validation Message */}
              {tinValidationMessage && (
                <div
                  style={{
                    marginTop: 8,
                    marginBottom: 16,
                    padding: "8px 12px",
                    borderRadius: 4,
                    fontSize: "14px",
                    backgroundColor: tinValidationMessage.includes("✓") ? "#d4edda" : "#fff3cd",
                    border: `1px solid ${tinValidationMessage.includes("✓") ? "#28a745" : "#ffc107"}`,
                    display: "flex",
                    alignItems: "center",
                    color: tinValidationMessage.includes("✓") ? "#155724" : "#856404",
                  }}
                >
                  {tinValidationMessage}
                </div>
              )}
              <FormCheckBox
                label="GIIN:"
                name="GIINCheckBox"
                checked={!!formData["GIINCheckBox"]}
                onChange={handleChange}
              />
              <FormInput
                label="GIIN Entered:"
                id="GIIN"
                name="GIIN"
                value={(formData["GIIN"] as string) || ""}
                onChange={handleChange}
              />

              {/* GIIN Validation Message */}
              {giinValidationMessage && (
                <div
                  style={{
                    marginTop: 8,
                    marginBottom: 16,
                    padding: "8px 12px",
                    borderRadius: 4,
                    fontSize: "14px",
                    backgroundColor: giinValidationMessage.includes("✓") ? "#d4edda" : "#fff3cd",
                    border: `1px solid ${giinValidationMessage.includes("✓") ? "#28a745" : "#ffc107"}`,
                    display: "flex",
                    alignItems: "center",
                    color: giinValidationMessage.includes("✓") ? "#155724" : "#856404",
                  }}
                >
                  {giinValidationMessage}
                </div>
              )}

              <FormCheckBox
                label={'GIIN "Applied For":'}
                name="GIIN Applied For"
                checked={!!formData["GIIN Applied For"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label="Foreign TIN:"
                name="Foreign TIN"
                checked={!!formData["Foreign TIN"]}
                onChange={handleChange}
              />
              <FormInput
                label="Foreign taxpayer id:"
                id="Foreign tax identifying number"
                name="Foreign tax identifying number"
                value={(formData["Foreign tax identifying number"] as string) || ""}
                onChange={handleChange}
              />

              <FormInput
                label="Reference numbers:"
                id="EXP-Reference_numbers"
                name="EXP-Reference_numbers"
                value={(formData["EXP-Reference_numbers"] as string) || ""}
                onChange={handleChange}
              />
            </section>

            {/* Part II Qualification Statement for Chapter 3 Status */}
            <section style={sectionStyle}>
              <FormTitle value={"Part II Qualification Statement for Chapter 3 Status"} />
              <div style={{ fontWeight: 600, marginBottom: 8 }}>10 For a foreign government</div>
              <FormCheckBox
                label="10a: Foreign government"
                name="EXP-10A_Foreign_government"
                checked={!!formData["EXP-10A_Foreign_government"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label="10b: Integral part"
                name="EXP-10B_Integral_part"
                checked={!!formData["EXP-10B_Integral_part"]}
                onChange={handleChange}
              />

              <CountryDropdown
                label="10b Country:"
                name="EXP-10B_Integral_part_government_name"
                id="EXP-10B_Integral_part_government_name"
                value={
                  typeof formData["EXP-10B_Integral_part_government_name"] === "string"
                    ? formData["EXP-10B_Integral_part_government_name"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "EXP-10B_Integral_part_government_name",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label="10b Country Other:"
                id="EXP-10B_Integral_part_government_name_other"
                name="EXP-10B_Integral_part_government_name_other"
                value={(formData["EXP-10B_Integral_part_government_name_other"] as string) || ""}
                onChange={handleChange}
              />
              <FormCheckBox
                label="10c: Controlled entity"
                name="EXP-10C_Controlled_entity"
                checked={!!formData["EXP-10C_Controlled_entity"]}
                onChange={handleChange}
              />

              <CountryDropdown
                label="10c Country:"
                name="EXP-10C_Controlled_entity_government_name"
                id="EXP-10C_Controlled_entity_government_name"
                value={
                  typeof formData["EXP-10C_Controlled_entity_government_name"] === "string"
                    ? formData["EXP-10C_Controlled_entity_government_name"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "EXP-10C_Controlled_entity_government_name",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label="10c Country Other:"
                id="EXP-10C_Controlled_entity_government_name_other"
                name="EXP-10C_Controlled_entity_government_name_other"
                value={
                  (formData["EXP-10C_Controlled_entity_government_name_other"] as string) || ""
                }
                onChange={handleChange}
              />
              <FormCheckBox
                label="10d: Not commercial activities"
                name="EXP-10D_Not_commercial_activities"
                checked={!!formData["EXP-10D_Not_commercial_activities"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label="10e: Central bank not US commercial"
                name="EXP-10E_Central_bank_not_us_commercial"
                checked={!!formData["EXP-10E_Central_bank_not_us_commercial"]}
                onChange={handleChange}
              />

              <div style={{ fontWeight: 600, marginBottom: 8, marginTop: 16 }}>
                11 For an international organization
              </div>
              <FormCheckBox
                label="11: International organization"
                name="EXP-11_International_organization"
                checked={!!formData["EXP-11_International_organization"]}
                onChange={handleChange}
              />

              <div style={{ fontWeight: 600, marginBottom: 8, marginTop: 16 }}>
                12 For a foreign central bank of issue
              </div>
              <FormCheckBox
                label="12: Foreign central bank"
                name="EXP-12_Foreign_central_bank"
                checked={!!formData["EXP-12_Foreign_central_bank"]}
                onChange={handleChange}
              />

              <div style={{ fontWeight: 600, marginBottom: 8, marginTop: 16 }}>
                13 For a foreign tax-exempt organisation, including foreign private foundations
              </div>
              <FormCheckBox
                label="13a: IRS determination"
                name="EXP-13A_Irs_determination"
                checked={!!formData["EXP-13A_Irs_determination"]}
                onChange={handleChange}
              />
              {/* <FormInput label="13a IRS determination letter text:" id="EXP-13A_Text" name="EXP-13A_Text" value={formData["EXP-13A_Text"] as string || ''} onChange={handleChange} /> */}
              <FormDate
                label="13a Date:"
                name="EXP-13A_tax_exempt_determination_letter_date"
                value={(formData["EXP-13A_tax_exempt_determination_letter_date"] as string) || ""}
                onChange={handleChange}
              />
              <FormCheckBox
                label="13b: US counsel opinion"
                name="EXP-13B_Us_counsel_opinion"
                checked={!!formData["EXP-13B_Us_counsel_opinion"]}
                onChange={handleChange}
              />
              {/* <FormInput label="13b US counsel opinion text:" id="EXP-13B_Text" name="EXP-13B_Text" value={formData["EXP-13B_Text"] as string || ''} onChange={handleChange} /> 
              <FormDate label="13b Date:" name="EXP-13B_Date" value={formData["EXP-13B_Date"] as string || ''} onChange={handleChange} />*/}
              <FormCheckBox
                label="13c: Not private foundation"
                name="EXP-13C_Not_private_foundation"
                checked={!!formData["EXP-13C_Not_private_foundation"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label="13d: Is private foundation"
                name="EXP-13D_Is_private_foundation"
                checked={!!formData["EXP-13D_Is_private_foundation"]}
                onChange={handleChange}
              />

              <div style={{ fontWeight: 600, marginBottom: 8, marginTop: 16 }}>
                14 For a government of a U.S. territory
              </div>
              <FormCheckBox
                label="14: US territory government"
                name="EXP-14_Us_territory_government"
                checked={!!formData["EXP-14_Us_territory_government"]}
                onChange={handleChange}
              />

              <div style={{ fontWeight: 600, marginBottom: 8, marginTop: 16 }}>
                15 For a withholding qualified holder
              </div>
              <FormCheckBox
                label="15a: Qualified holder"
                name="EXP-15A_Qualified_holder"
                checked={!!formData["EXP-15A_Qualified_holder"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label="15b: Foreign partnership qualified holders"
                name="EXP-15B_Foreign_partnership_qualified_holders"
                checked={!!formData["EXP-15B_Foreign_partnership_qualified_holders"]}
                onChange={handleChange}
              />
            </section>

            {/* Part III Qualification Statement for Chapter 4 Status */}
            <section style={sectionStyle}>
              <FormTitle
                value={"Part III Qualification Statement for Chapter 4 Status (if required)"}
              />

              <FormCheckBox
                label="16: For a nonreporting IGA FFI"
                name="EXP-16_Nonreporting_IGA_FFI"
                checked={!!formData["EXP-16_Nonreporting_IGA_FFI"]}
                onChange={handleChange}
              />

              <CountryDropdown
                label="16 Country:"
                name="EXP-16_nonreporting_iga_ffi_country"
                id="EXP-16_nonreporting_iga_ffi_country"
                value={
                  typeof formData["EXP-16_nonreporting_iga_ffi_country"] === "string"
                    ? formData["EXP-16_nonreporting_iga_ffi_country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "EXP-16_nonreporting_iga_ffi_country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label="16 Treated as:"
                id="EXP-16_nonreporting_iga_ffi_treatment"
                name="EXP-16_nonreporting_iga_ffi_treatment"
                value={(formData["EXP-16_nonreporting_iga_ffi_treatment"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label="16 GIIN:"
                id="EXP-16_nonreporting_iga_ffi_giin"
                name="EXP-16_nonreporting_iga_ffi_giin"
                value={(formData["EXP-16_nonreporting_iga_ffi_giin"] as string) || ""}
                onChange={handleChange}
              />

              <FormCheckBox
                label="17: For a territory financial institution"
                name="EXP-17_Territory_financial_institution"
                checked={!!formData["EXP-17_Territory_financial_institution"]}
                onChange={handleChange}
              />

              <FormCheckBox
                label="18: For a foreign government"
                name="EXP-18_Foreign_government_fatca"
                checked={!!formData["EXP-18_Foreign_government_fatca"]}
                onChange={handleChange}
              />

              <div style={{ fontWeight: 600, marginBottom: 8, marginTop: 16 }}>
                19 For a exempt retirement plan of a foreign government
              </div>
              <FormCheckBox
                label="19a: Retirement plan employees"
                name="EXP-19A_Retirement_plan_employees"
                checked={!!formData["EXP-19A_Retirement_plan_employees"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label="19b: Entity identifier"
                name="EXP-19B_Entity_identitfier"
                checked={!!formData["EXP-19B_Entity_identitfier"]}
                onChange={handleChange}
              />

              <FormCheckBox
                label="20: For a 501(c) organization"
                name="EXP-20_501c_Organization_fatca"
                checked={!!formData["EXP-20_501c_Organization_fatca"]}
                onChange={handleChange}
              />

              <div style={{ fontWeight: 600, marginBottom: 8, marginTop: 16 }}>
                21 For a passive NFFE
              </div>
              <FormCheckBox
                label="21a: Passive NFFE certification"
                name="EXP-21A_Passive_nffe_certification"
                checked={!!formData["EXP-21A_Passive_nffe_certification"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label="21b: No substantial US owners"
                name="EXP-21B_No_substantial_us_owners"
                checked={!!formData["EXP-21B_No_substantial_us_owners"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label="21c: Has substantial US owners"
                name="EXP-21C_Has_substantial_us_owners"
                checked={!!formData["EXP-21C_Has_substantial_us_owners"]}
                onChange={handleChange}
              />

              <FormCheckBox
                label="22: Sponsored direct reporting"
                name="EXP-22_Sponsored_direct_reporting"
                checked={!!formData["EXP-22_Sponsored_direct_reporting"]}
                onChange={handleChange}
              />
              <FormInput
                label="22 Name of Sponsoring Entity:"
                id="EXP-22_sponsoring_entity_name"
                name="EXP-22_sponsoring_entity_name"
                value={(formData["EXP-22_sponsoring_entity_name"] as string) || ""}
                onChange={handleChange}
              />
            </section>

            <section style={sectionStyle}>
              <FormTitle value={"Part IV Certification"} />
              <div style={{ marginBottom: 24 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 12,
                    flexDirection: "column",
                  }}
                >
                  <div style={{ marginRight: 8 }}>
                    {" "}
                    <b>
                      I agree that I will submit a new form within 30 days if any certification on
                      this form becomes incorrect.
                    </b>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", flexDirection: "row" }}>
                    <input
                      type="checkbox"
                      id="Signing_capacity"
                      checked={formData.Signing_capacity}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "Signing_capacity",
                            type: "checkbox",
                            checked: e.target.checked,
                          },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                      style={{ marginRight: 8 }}
                    />
                    <label htmlFor="certify" style={{ fontWeight: 500 }}>
                      I certify that I have the capacity to sign for the entity identified on line 1
                      of this form.
                    </label>
                  </div>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <b>
                    Is signed: <span style={{ color: "red" }}>*</span>
                  </b>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="yes"
                      checked={formData.isSigned === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="no"
                      checked={formData.isSigned === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                {showValidationErrors && errors["isSigned"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4, marginBottom: 12 }}>
                    {errors["isSigned"]}
                  </div>
                )}
                <div style={{ marginBottom: 8 }}>
                  <b>
                    Has the form certification been modified:{" "}
                    <span style={{ color: "red" }}>*</span>
                  </b>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="yes"
                      checked={formData.isModified === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="no"
                      checked={formData.isModified === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                {showValidationErrors && errors["isModified"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4, marginBottom: 12 }}>
                    {errors["isModified"]}
                  </div>
                )}
                <FormInput
                  label={"Signed Name:"}
                  id={"Print_name"}
                  value={formData["Print_name"]}
                  name={"Print_name"}
                  onChange={handleChange}
                />
                {/* <FormInput label={"Signature:"} id={"Signature"} value={formData["Signature"]} name={"Signature"} onChange={handleChange} /> */}

                <div style={{ marginBottom: "1rem" }}>
                  <FormDate
                    label="Date:"
                    name="Signature_date"
                    value={formData["Signature_date"] || ""}
                    onChange={handleChange}
                  />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 12,
                      marginTop: 12,
                    }}
                  >
                    <input
                      type="checkbox"
                      id="Signature_unreadable"
                      checked={formData.Signature_unreadable || false}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "Signature_unreadable",
                            type: "checkbox",
                            checked: e.target.checked,
                          },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                      style={{ marginRight: 8 }}
                    />
                    <label htmlFor="Signature_unreadable" style={{ fontWeight: 500 }}>
                      Check if signature is unreadable
                    </label>
                  </div>
                </div>
              </div>
            </section>

            <div
              style={{
                display: "flex",
                marginTop: 20,
                marginBottom: 40,
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                maxWidth: "100%",
                alignItems: "center",
                boxSizing: "border-box",
              }}
            >
              <button
                type="submit"
                style={{
                  padding: "6px 16px",
                  backgroundColor: InstaColors.primary,
                  color: "white",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginRight: "auto",
                  borderRadius: "0.375rem",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  cursor: "pointer",
                  border: "none",
                }}
              >
                Save Form
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default W8EXPFORM;
